/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {TableModel} from './models/table.model';
import {TableSortedByModel} from './models/table-sorted-by.model';
import {TableLineModel} from './models/table-line.model';
import {TableStateModel} from './models/table-state.model';
import {StatefulComponent} from '../stateful/stateful.component';
import {
    Subscription,
    timer
} from 'rxjs';
import {TableService} from './table-service/table.service';
import {TableLineComponent} from './table-line/table-line.component';
import {distinctUntilChanged} from 'rxjs/operators';

@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table-global-styles.css', './table.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TableComponent<Enum, DataObject> extends StatefulComponent<TableStateModel<Enum, DataObject>>
        implements OnDestroy, AfterViewChecked, OnInit {
    /**
     * Template to add action buttons to the top of the table. Clear all filter button will always appear on the dar right
     */
    @Input() actionButtonsTemplate: TemplateRef<any>;
    /**
     * If the button to clear all filters should appear
     */
    @Input() hasClearAllButton = false;
    /**
     * {@link TableModel} to populate the table component
     */
    @Input() tableData: TableModel<Enum, DataObject>;
    /**
     * Set of table columns to exclude in case there is a need to reduce the table width
     * It is initialized empty
     */
    @Input() excludedColumnsOnResize: Enum[] = [];
    /**
     * Received event that will allow to exclude the columns from excludedColumnsOnResize and reduce the table width
     */
    @Input() reduceTableColumns: EventEmitter<boolean>;
    /**
     * {@link TemplateRef} of the component to be showed in table lines accordion
     */
    @Input() accordionLineTemplate: TemplateRef<any>;
    /**
     * {@link TableSortedByModel} of the initial sorting status
     */
    @Input() initiallySortedBy: TableSortedByModel<Enum>;
    /**
     * The table name - to be used when table body is empty
     */
    @Input() tableName: string;
    /**
     * Input event emitter to register scroll events
     */
    @Input() scrollEvent = new EventEmitter<void>();
    /**
     * If the table should show background blur
     */
    @Input() hasBackgroundBlur = false;
    /**
     * If the table should show all container background blur
     */
    @Input() hasAllTableBackgroundBlur = false;
    /**
     * If the accordion behaviour should be disabled
     */
    @Input() disableAccordion = false;
    /**
     * If is need to force close the accordion line
     */
    @Input() isToCloseAccordion = new EventEmitter<void>();

    /**
     * {@link EventEmitter} for when a line is opened. Emits the {@link TableLineModel}
     */
    @Output() selectedLine = new EventEmitter<TableLineModel<Enum, DataObject>>();

    /**
     * {@link EventEmitter} for when a line is mouse hover. Emits the {@link TableLineModel}
     */
    @Output() mouseHoverLine = new EventEmitter<TableLineModel<Enum, DataObject>>();

    /**
     * {@link EventEmitter} for when a line is mouse leave. Emits the {@link TableLineModel}
     */
    @Output() mouseLeaveLine = new EventEmitter<TableLineModel<Enum, DataObject>>();

    /**
     * {@link EventEmitter} for when a line is mouse hover and is open. Emits the {@link boolean}
     */
    @Output() mouseHoverOpenLine = new EventEmitter<boolean>();

    /**
     * {@link EventEmitter} for when the sorting has changed. Emits the {@link TableSortedByModel}
     */
    @Output() sortedBy = new EventEmitter<TableSortedByModel<Enum>>();

    /**
     * {@link EventEmitter} for when clear all filters button is clicked. Emits nothing
     */
    @Output() clearAllFiltersClick = new EventEmitter<void>();
    /**
     * If  mouse is over the open line
     */
    public isMouseHoverLine: boolean;

    private subscriptions = new Subscription();

    /**
     * Total table width percentage
     */
    private readonly TOTAL_PERCENTAGE_WIDTH = 100;

    /**
     * Determines if the table should show the body loading spinner.
     */
    get showTableBodyLoadingSpinner(): boolean {
        return this.state.isLoading;
    }

    /**
     * Returns if the table body is populated
     */
    get hasPopulatedBody(): boolean {
        return (this.tableData.staticSection != null && this.tableData.staticSection.length > 0)
                || (this.tableData.dynamicSection != null && this.tableData.dynamicSection.length > 0);
    }

    get hasNoResultBody(): boolean {
        return this.tableData?.noFilterResultsFoundTemplate ? true : false;
    }

    /**
     * Returns if the table should have a body section separator
     */
    get hasTableBodySectionSeparator(): boolean {
        return (this.tableData.staticSection != null && this.tableData.staticSection.length > 0)
                && (this.tableData.dynamicSection != null && this.tableData.dynamicSection.length > 0);
    }

    /**
     * Returns if the tables body dynamic section should show the empty table template
     */
    get showDynamicSectionEmptyTableTemplate(): boolean {
        return this.tableData.dynamicSection != null && this.tableData.dynamicSection.length === 0;
    }

    /**
     * Returns if exist table labels
     */
    get hasSecondHeader(): boolean {
        return this.tableData.labels?.labelsData != null && this.tableData.labels?.labelsData?.length > 0;
    }

    get hasFloatContent(): boolean {
        return this.retrieveHasFloatContentMap()?.size > 0;
    }

    get tableLinesLength(): number {
        let length = 0;
        if (this.tableData?.staticSection?.length != null) {
            length = this.tableData?.staticSection?.length;
        }
        return length;
    }

    /**
     * Retrieves the 'grid-template-columns' propriety from the {@link tableData} to set the table cells relative width
     */
    get columnStyles(): { [key: string]: any } {
        const tableValues = [...this.tableData.headers.entries.values()]
                .filter((val) => !this.state.excludedColumns.includes(val.code));
        const widthWeights = tableValues.map((value) => value.widthWeight);
        const widthWeightSum = widthWeights.reduce((sum, value) => sum + value, 0);
        const templateColumns = widthWeights.reduce((sum, value) => `${sum} ${value / widthWeightSum * 100}%`, '');

        return {
            'display': 'grid',
            'grid-template-columns': templateColumns,
            'align-items': 'center'
        };
    }

    constructor(private changeDetector: ChangeDetectorRef,
                private tableService: TableService<Enum, DataObject>) {
        super();
    }

    ngOnInit(): void {
        this.storeState({
            excludedColumns: []
        });
        this.reduceTableColumns?.pipe(distinctUntilChanged())
                .subscribe((reduce: boolean) => {
                    this.storeState({
                        excludedColumns: reduce ? this.excludedColumnsOnResize : []
                    });
                });
        this.closeAccordion();
    }

    ngAfterViewChecked(): void {
        this.changeDetector.detectChanges();
    }

    /**
     * Retrieves the dataPipes from the {@link tableData} and creates a new map to be passed to the table lines
     */
    retrieveDataPipesMap(): Map<Enum, any> {
        const pipesMap = new Map<any, any>();
        this.tableData.headers.entries.forEach((value, key) => {
            if (value.dataPipe != null) {
                pipesMap.set(key, value.dataPipe);
            }
        });
        return pipesMap;
    }

    /**
     * Retrieves the dots on status column from the {@link tableData} and creates a new map to be passed to the table lines
     */
    retrieveHasDotMap(): Map<Enum, boolean> {
        const hasDotMap = new Map<Enum, boolean>();
        this.tableData.headers.entries.forEach((value, key) => {
            if (value.hasDot) {
                hasDotMap.set(key, value.hasDot);
            }
        });
        return hasDotMap;
    }

    /**
     * Retrieves if the textAlignRight is called in a specific column from the {@link tableData} and creates a new map to be passed to
     * the table lines
     */
    retrieveHasTextAlignRightMap(): Map<Enum, boolean> {
        const hasTextAlignRight = new Map<Enum, boolean>();
        this.tableData.headers.entries.forEach((value, key) => {
            if (value.hasTextAlignRight) {
                hasTextAlignRight.set(key, value.hasTextAlignRight);
            }
        });

        return hasTextAlignRight;
    }

    /**
     * Retrieves if the hasFloatContent is called in a specific column from the {@link tableData} and creates a new map to be passed to
     * the table lines
     */
    retrieveHasFloatContentMap(): Map<Enum, boolean> {
        const hasFloatContent = new Map<Enum, boolean>();
        this.tableData.headers.entries.forEach((value, key) => {
            if (value.hasFloatContent) {
                hasFloatContent.set(key, value.hasFloatContent);
            }
        });

        return hasFloatContent;
    }

    /**
     * Retrieves if the hasCopyPasteContent is called in a specific column from the {@link tableData} and creates a new map to be passed to
     * the table lines
     */
    retrieveHasCopyPasteMap(): Map<Enum, boolean> {
        const hasCopyPasteContent = new Map<Enum, boolean>();
        this.tableData.headers.entries.forEach((value, key) => {
            if (value.hasCopyPaste) {
                hasCopyPasteContent.set(key, value.hasCopyPaste);
            }
        });
        return hasCopyPasteContent;
    }

    getStylesClass(line: TableLineModel<Enum, DataObject>, expandableLines: boolean): string {
        let baseClass = 'table-line';

        if (!expandableLines && !this.hasTableLineAvailableActions) {
            baseClass += ' no-expandable-lines';
        }
        if (line.isOpen) {
            baseClass += ' sticky expandBlocked';
        }
        if (this.isMouseHoverLine) {
            baseClass += ' isHoverLine';
        } else if (line.isMouseHover){
            baseClass += ' isClosed isHoverLine';
        }

        if (this.hasFloatContent) {
            baseClass += ' hasFloatContent ';
        }

        return baseClass;
    }

    /**
     * Returns {@link boolean} if a given line accordion is opened
     */
    isLineSelected(line: TableLineModel<Enum, DataObject>): boolean {
        return this.state.selectedLine === line;
    }

    /**
     * Toggles the table body loading
     * @param isLoading is the table body is loading
     */
    toggleTableBodyLoading(isLoading: boolean): void {
        this.storeState({isLoading: isLoading});
    }

    /**
     * Sets the table sortedBy attribute
     */
    sortBy(sortedBy: TableSortedByModel<Enum>): void {
        this.storeState({sortedBy: sortedBy?.code});
        timer(0)
                .subscribe(() => {
                    this.sortedBy.emit(sortedBy);
                });
    }

    /**
     * Manages the line accordion status change
     * @param line the selected line {@link TableLineModel}
     * @param isOpen is the selected line is open {@link boolean}
     * @param lineComponent the selected {@link TableLineComponent}
     */
    accordionStatusChange(line: TableLineModel<Enum, DataObject>,
                          isOpen: boolean,
                          lineComponent: TableLineComponent<Enum, DataObject>): void {
        if (isOpen) {
            if (this.state.selectedLine != null) {
                this.state.selectedLine.isOpen = false;
                this.state.selectedLine.accordionData = null;
            }
            this.storeState({selectedLine: line});
            this.selectedLine.emit(line);
            this.tableService.openEvent.emit(lineComponent);
        } else {
            this.storeState({selectedLine: null});
            this.selectedLine.emit(null);
        }
    }

    /**
     * Close the select line accordion
     */
    closeAccordion(): void {
        this.subscriptions.add(
                this.isToCloseAccordion.subscribe(() => {
                    if (this.state.selectedLine != null) {
                        this.state.selectedLine.accordionData = null;
                        this.state.selectedLine.isOpen = false;
                    }
                    this.storeState({selectedLine: null});
                    this.selectedLine.emit(null);
                })
        );
    }

    lineHasBackgroundBlur(line: TableLineModel<Enum, DataObject>): boolean {
        let isBlurred = false;
        if (this.hasBackgroundBlur) {
            isBlurred = !line.isOpen;
        }
        return isBlurred;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    getDataCyLineElement(index: string): string {
        return `table-line-${index}`;
    }

    getScrollToLineElement(line: TableLineModel<Enum, DataObject>): string {
        if (line.identifier != null) {
            return `scroll-${line.identifier}`;
        }
    }

    /**
     * Update isMouseHoverLine if mouse is over opened line
     */
    changeOpenLineBackgroundStyle(event: boolean, line: TableLineModel<Enum, DataObject>): void {
        this.isMouseHoverLine = event;
        this.mouseHoverLine.emit(line);
        this.mouseHoverOpenLine.emit(event);
    }

    changeMouseHoverLineBackgroundStyle(event: boolean, line: TableLineModel<Enum, DataObject>): void {
        this.mouseLeaveLine.emit(line);
    }

    lineIsAnimated(line: TableLineModel<Enum, DataObject>): boolean {
        return line.isAnimated;
    }

    lineIsMouseHover(line: TableLineModel<Enum, DataObject>): boolean {
        return line.isMouseHover;
    }

    private hasTableLineAvailableActions(): boolean {
        return this.tableData?.tableLineAvailableActions?.length > 0;
    }
}
